/**
 * Strict validation for a caller-supplied return path before a redirect or an `href`.
 *
 * Client-safe: pure string and `URL` work, no server imports.
 */

// Unresolvable base origin for redirect checks. `.invalid` is reserved by RFC 2606.
export const REDIRECT_PROBE_ORIGIN = "https://x.invalid";

// C0 control chars end here; DEL is 0x7F
const LAST_C0_CONTROL = 0x1f;
const DEL = 0x7f;

/**
 * Returns the same-origin path (`/a/b?c#d`) for `candidate`, or null. Fails closed.
 *
 * Stricter than `getSafeRedirectPath`: it rejects rather than normalizes. It checks the raw
 * path and the path decoded once, so an encoded `%09` or `%2F%2F` fails the same way as the
 * literal character. The strict check stops at the first `?` or `#`: only the path can turn
 * into `//`, and a query like `?search=John%20Doe` or `?q=50%` is a real page to return to.
 * The path that comes back keeps the raw encoding of `candidate`.
 */
export function toSafeInternalPath(candidate: string | null | undefined): string | null {
  if (!candidate?.length) return null;

  const pathEnd = candidate.search(/[?#]/);
  const path = pathEnd === -1 ? candidate : candidate.slice(0, pathEnd);
  const rest = pathEnd === -1 ? "" : candidate.slice(pathEnd);

  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(path);
  } catch {
    return null;
  }
  if (!isStrictRelativePath(path) || !isStrictRelativePath(decodedPath)) return null;
  // the query is not decoded, but a literal control char has no place in a URL
  if ([...rest].some(isControlChar)) return null;

  // Resolve against a placeholder origin and keep the path only if the origin does not move.
  let resolved: URL;
  try {
    resolved = new URL(candidate, REDIRECT_PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (resolved.origin !== REDIRECT_PROBE_ORIGIN) return null;
  // "/.//evil.com" normalizes to the pathname "//evil.com", which is protocol-relative
  if (!resolved.pathname.startsWith("/") || resolved.pathname.startsWith("//")) return null;

  return resolved.pathname + resolved.search + resolved.hash;
}

/** One leading "/" (not "//" or "/\"), and no control char, whitespace, or backslash. */
function isStrictRelativePath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  return ![...path].some(isUnsafePathChar);
}

// browsers strip or rewrite these, so "/\t/evil.com" and "/\evil.com" both become "//evil.com"
function isUnsafePathChar(char: string): boolean {
  if (isControlChar(char)) return true;
  return char === "\\" || char.trim() === "";
}

function isControlChar(char: string): boolean {
  const code = char.charCodeAt(0);
  return code <= LAST_C0_CONTROL || code === DEL;
}
